import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  archiveContact,
  blockContact,
  checkContactMassDelete,
  createContact,
  createContactGroup,
  createContactTag,
  deleteContactById,
  deleteContactGroup,
  deleteContactMassDelete,
  deleteContactTag,
  executeImportContact,
  getContact,
  getContactCanBeDeleted,
  getContactDetail,
  getContactGroup,
  getContactReportsGraph,
  getContactStats,
  getContactTags,
  getContactTemplateMessages,
  getSuggestionContact,
  getSummaryContact,
  moveContactGroup,
  unarchiveContact,
  updateContact,
  updateContactGroup,
  uploadImportContact,
} from "apis/contact";
import { useCallback } from "react";
import { useAntdMessage } from "hooks/useAntdMessage";

export function useContactTags(id, options = {}) {
  const { message } = useAntdMessage();
  const { t } = useTranslation();
  return useQuery({
    queryKey: ["contactTags", id],
    queryFn: async () => {
      let response;
      try {
        const {
          data: { data },
        } = await getContactTags(id);
        response = data;
      } catch (error) {
        throw new Error(t("error.failed_to_load_data_from_server"));
      }
      return response;
    },
    throwOnError: (error) => {
      return message.error(
        error.message || t("error.failed_to_load_data_from_server")
      );
    },
    ...options,
  });
}

export const useCreateContactTag = (options = {}) => {
  // const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["createContactTag"],
    mutationFn: (payload) => createContactTag(payload),
    // onSuccess: (response, variables) => {
    //   if (response.data.success) {
    //     queryClient.invalidateQueries([
    //       "getContactDetail",
    //       { id: variables.id },
    //     ]);
    //   }
    // },
    ...options,
  });
};

export const useDeleteContactTag = (options = {}) => {
  // const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["deleteContactTag"],
    mutationFn: (payload) => deleteContactTag(payload),
    // onSuccess: (response, variables) => {
    //   queryClient.invalidateQueries(["getContactDetail", { id: variables.id }]);
    // },
    ...options,
  });
};

export const useContactSummary = (options = {}) => {
  const { message } = useAntdMessage();
  const { t } = useTranslation();
  return useQuery({
    queryKey: ["summaryContacts"],
    queryFn: async () => {
      let response;
      try {
        const {
          data: { data },
        } = await getSummaryContact();
        response = data;
      } catch (error) {
        throw new Error(t("error.failed_to_load_data_from_server"));
      }
      return response;
    },
    throwOnError: (error) => {
      return message.error(
        error.message || t("error.failed_to_load_data_from_server")
      );
    },
    ...options,
  });
};
export const useContactGroup = ({ payload = {} }, options = {}) => {
  const { message } = useAntdMessage();
  const { t } = useTranslation();
  return useQuery({
    queryKey: ["contactGroup", payload],
    queryFn: async () => {
      let response;
      try {
        const {
          data: { data },
        } = await getContactGroup(payload);
        response = data;
      } catch (error) {
        throw new Error(t("error.failed_to_load_data_from_server"));
      }
      return response;
    },
    throwOnError: (error) => {
      return message.error(
        error.message || t("error.failed_to_load_data_from_server")
      );
    },
    ...options,
  });
};
export const useCreateContactGroup = (options = {}) => {
  return useMutation({
    mutationFn: (payload) => createContactGroup(payload),
    ...options,
  });
};
export const useUpdateContactGroup = (options = {}) => {
  return useMutation({
    mutationFn: (payload) => updateContactGroup({ payload, id: payload.id }),
    ...options,
  });
};
export const useContactCanBeDeleted = (options = {}) => {
  const { message } = useAntdMessage();
  const { t } = useTranslation();
  return useQuery({
    queryKey: ["contactGroupCanBeDeleted"],
    queryFn: async () => {
      let response;
      try {
        const {
          data: { data },
        } = await getContactCanBeDeleted();
        response = data;
      } catch (error) {
        throw new Error(t("error.failed_to_load_data_from_server"));
      }
      return response;
    },
    throwOnError: (error) => {
      return message.error(
        error.message || t("error.failed_to_load_data_from_server")
      );
    },
    ...options,
  });
};

export const useDeleteContactGroup = (options = {}) => {
  return useMutation({
    mutationFn: (payload) => deleteContactGroup(payload),
    ...options,
  });
};
export const useMoveContactGroup = (options = {}) => {
  return useMutation({
    mutationFn: (payload) =>
      moveContactGroup({
        id: payload.id,
        dest_id: payload.dest_id,
      }),
    ...options,
  });
};

export const useGetContact = ({ payload = {} }, options = {}) => {
  const { message } = useAntdMessage();
  const { t } = useTranslation();
  return useQuery({
    queryKey: ["getContact", payload],
    queryFn: async () => {
      let response;
      try {
        const {
          data: { data },
        } = await getContact(payload);
        response = data;
      } catch (error) {
        throw new Error(t("error.failed_to_load_data_from_server"));
      }
      return response;
    },
    throwOnError: (error) => {
      return message.error(
        error.message || t("error.failed_to_load_data_from_server")
      );
    },
    ...options,
  });
};
export const useGetContactDetail = (id, options = {}) => {
  const { t } = useTranslation();
  return useQuery({
    queryKey: ["getContactDetail", id],
    queryFn: async () => {
      let response;
      try {
        const {
          data: { data },
        } = await getContactDetail(id);
        response = data;
      } catch (error) {
        throw new Error(t("error.failed_to_load_data_from_server"));
      }
      return response;
    },
    ...options,
  });
};

export const useCreateContact = (options = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["createNewContact"],
    mutationFn: (payload) => createContact(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["getContact"] });
      queryClient.invalidateQueries({ queryKey: ["contactReportingsGraphs"] });
      queryClient.invalidateQueries({ queryKey: ["summaryContacts"] });
    },
    ...options,
  });
};

export const useCheckContactMassDelete = (options = {}) => {
  return useMutation({
    mutationKey: ["checkContactMassDelete"],
    mutationFn: (payload) => checkContactMassDelete(payload),
    ...options,
  });
};

export const useDeleteContactMassDelete = (options = {}) => {
  return useMutation({
    mutationKey: ["deleteContactMassDelete"],
    mutationFn: (payload) => deleteContactMassDelete(payload),
    ...options,
  });
};

export const useUploadImportContact = (options = {}) => {
  return useMutation({
    mutationKey: ["uploadImportContact"],
    mutationFn: (formData) => uploadImportContact(formData),
    ...options,
  });
};

export const useExecuteImportContact = (options = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["executeImportContact"],
    mutationFn: (payload) => executeImportContact(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["getContact"] });
      queryClient.invalidateQueries({ queryKey: ["summaryContacts"] });
    },
    ...options,
  });
};

export const useContactSuggestion = (payload = {}, options = {}) => {
  const { message } = useAntdMessage();
  const { t } = useTranslation();
  return useQuery({
    queryKey: ["contactSuggestion", payload],
    queryFn: async () => {
      let response;
      try {
        const {
          data: { data },
        } = await getSuggestionContact(payload);
        response = data;
      } catch (error) {
        throw new Error(t("error.failed_to_load_data_from_server"));
      }
      return response;
    },
    throwOnError: (error) => {
      return message.error(
        error.message || t("error.failed_to_load_data_from_server")
      );
    },
    ...options,
  });
};

//contact reporting
export const useContactReportingGraph = ({
  payload = {},
  extraQueryKey = [],
  options = {},
}) => {
  const { message } = useAntdMessage();
  const { t } = useTranslation();
  return useQuery({
    queryKey: ["contactReportingsGraphs", payload, ...extraQueryKey],
    queryFn: async () => {
      let response;
      try {
        const {
          data: { data },
        } = await getContactReportsGraph(payload);
        response = data;
      } catch (error) {
        throw new Error(t("error.failed_to_load_data_from_server"));
      }
      return response;
    },
    throwOnError: (error) => {
      return message.error(
        error.message || t("error.failed_to_load_data_from_server")
      );
    },
    ...options,
  });
};

//summmary detail
export const useContactDetailSummary = ({ payload = {} }, options = {}) => {
  const { t } = useTranslation();
  const { message } = useAntdMessage();
  return useQuery({
    queryKey: ["summaryContactsDetail", payload],
    queryFn: async () => {
      let response;
      try {
        const {
          data: { data },
        } = await getContactStats(payload.id);
        response = data;
      } catch (error) {
        throw new Error(t("error.failed_to_load_data_from_server"));
      }
      return response;
    },
    throwOnError: (error) => {
      return message.error(
        error.message || t("error.failed_to_load_data_from_server")
      );
    },
    ...options,
  });
};

export const useDeleteContact = (options = {}) => {
  return useMutation({
    mutationFn: (payload) => deleteContactById(payload),
    ...options,
  });
};

export const useArchiveContact = (options = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => archiveContact(payload),
    onSuccess: (response, variables) => {
      const oldData = queryClient.getQueryData(["getContactDetail", variables]);
      queryClient.setQueryData(["getContactDetail", variables], {
        ...oldData,
        is_archive: 1,
      });
    },
    ...options,
  });
};

export const useUnarchiveContact = (options = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => unarchiveContact(payload),
    onSuccess: (response, variables) => {
      const oldData = queryClient.getQueryData(["getContactDetail", variables]);
      queryClient.setQueryData(["getContactDetail", variables], {
        ...oldData,
        is_archive: 0,
      });
    },
    ...options,
  });
};

export const useUpdateContact = (options = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["useUpdateContact"],
    mutationFn: (payload) => updateContact(payload),
    onSuccess: (response, variables) => {
      const oldData = queryClient.getQueryData([
        "getContactDetail",
        variables.id,
      ]);
      queryClient.setQueryData(["getContactDetail", variables.id], {
        ...oldData,
        ...response.data.data,
      });
    },
    ...options,
  });
};

export const useDetailContactCache = () => {
  const queryClient = useQueryClient();
  const appendTagsToContactDetail = useCallback(
    ({ id, ...payload }) => {
      queryClient.setQueryData(
        ["getContactDetail", id.toString()],
        (oldData) => {
          return { ...oldData, ...payload };
        }
      );
    },
    [queryClient]
  );
  return {
    appendTagsToContactDetail,
  };
};

export const useInfiniteContacts = (payload, options = {}) => {
  const { message } = useAntdMessage();
  return useInfiniteQuery({
    initialPageParam: 1,
    queryKey: ["infinite/contacts", payload],
    queryFn: async ({ pageParam }) => {
      let response;
      try {
        const {
          data: { data },
        } = await getContact({
          page: pageParam,
          ...payload,
        });
        response = data;
      } catch (error) {
        throw new Error("Failed to load data from server!");
      }
      return response;
    },
    getNextPageParam: (lastPage) => {
      if (!lastPage) {
        return undefined;
      }
      return lastPage.current_page < lastPage.last_page
        ? lastPage.current_page + 1
        : undefined;
    },
    onError: (error) =>
      message.error(
        error?.message ||
          error?.data?.message ||
          "Failed to load data from server!"
      ),
    keepPreviousData: true,
    ...options,
  });
};

export const useBlockContact = (options = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => blockContact(payload),
    onSuccess: (response, variables) => {
      const oldData = queryClient.getQueryData([
        "getContactDetail",
        variables.id,
      ]);
      queryClient.setQueryData(["getContactDetail", variables.id], {
        ...oldData,
        is_blocked: variables.is_block ? true : false,
      });
    },
    ...options,
  });
};

export function useContactTemplateMessages(id, options = {}) {
  return useQuery({
    queryKey: ["contactTemplateMessages", id],
    queryFn: async () => {
      const { data } = await getContactTemplateMessages(id);
      return data?.data ?? [];
    },
    staleTime: 60_000,
    ...options,
  });
}
